import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled from '@emotion/styled';
import CustomModal from '@/components/common/elements/Modal';
import { userAtom } from '@/state/loginState';
import { designSystem } from '@/styles/theme';

/**
 * 휴대폰 번호가 없는 기존 회원에게 등록을 권한다(2026-09-27 — 가입 때 선택 입력이던 시기에 비워 둔 회원이 많다).
 * 강제는 아니다: "나중에"를 누르면 7일, "등록하기"로 이동하면 1일 동안 다시 띄우지 않는다.
 * 치과 회원은 가입 때 병원 번호가 들어가므로 대상이 아니다.
 */
const SNOOZE_KEY = 'mc_phone_prompt_snooze_until';
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * 둘러보는 화면(홈·목록)에서만 띄운다 — 글쓰기·지원·결제처럼 입력 중인 화면에서 갑자기 뜨지 않게.
 * 정확히 이 경로일 때만(상세·작성 화면 제외).
 */
const PROMPT_PATHS: string[] = ['/', '/recruitments', '/recruitments/region', '/recruitments/welfare', '/posts', '/seminars', '/expert-lounge', '/notices', '/profile', '/salary-surveys'];

/** 저장소가 막힌 환경(사생활 보호 모드 등)에서도 이번 방문 동안은 다시 안 뜨게 */
let memorySnoozeUntil = 0;

const snoozed = (): boolean => {
    if (memorySnoozeUntil > Date.now()) return true;
    try {
        return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now();
    } catch {
        return false;
    }
};

const snooze = (days: number): void => {
    memorySnoozeUntil = Date.now() + days * DAY_MS;
    try {
        localStorage.setItem(SNOOZE_KEY, String(memorySnoozeUntil));
    } catch {
        // 저장이 막힌 환경 — 메모리 값으로 이번 방문 동안만 막는다
    }
};

const PhoneNumberPrompt = () => {
    const user = useRecoilValue(userAtom);
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!user || user.hasPhoneNumber !== false || user.clinics) return;
        const path = location.pathname.replace(/\/+$/, '') || '/';
        if (!PROMPT_PATHS.includes(path)) return;
        if (snoozed()) return;
        setOpen(true);
    }, [user, location.pathname]);

    if (!open) return null;

    return (
        <CustomModal title="휴대폰 번호를 등록해 주세요" open={open} onClose={() => { snooze(7); setOpen(false); }} height="auto">
            <Body>
                <Lead>채용 지원이나 세미나 신청 후 치과·강사가 연락할 때 필요해요. 1분이면 끝나요.</Lead>
                <Actions>
                    <Later type="button" onClick={() => { snooze(7); setOpen(false); }}>나중에</Later>
                    <Go
                        type="button"
                        onClick={() => {
                            snooze(1);
                            setOpen(false);
                            navigate('/profile/user/edit');
                        }}
                    >
                        등록하기
                    </Go>
                </Actions>
            </Body>
        </CustomModal>
    );
};

export default PhoneNumberPrompt;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 0;
`;

const Lead = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #374151;
    word-break: keep-all;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;

    & > button { flex: 1; }
`;

const Later = styled.button`
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #374151;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    padding: 13px;
    cursor: pointer;
`;

const Go = styled.button`
    border: none;
    background: ${designSystem.colors.primary};
    color: #fff;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    padding: 13px;
    cursor: pointer;
`;
